import React, { useEffect, useState, useRef } from "react";
import "./ProjectsPage.css";
import { Search, X } from "lucide-react";

export default function AdminProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [assets, setAssets] = useState([]);
  const [search, setSearch] = useState("");
  const detailRef = useRef(null);

  const [clients, setClients] = useState([]);
  const [managers, setManagers] = useState([]);
  const user = JSON.parse(localStorage.getItem("user")) || {};

  async function apiFetch(url, opts = {}) {
    const token = localStorage.getItem("token");
    const headers = { ...(opts.headers || {}) };
    if (opts.json !== false && opts.body !== undefined) {
      headers["Content-Type"] = headers["Content-Type"] || "application/json";
    }
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(url, { ...opts, headers });
    const contentType = res.headers.get("content-type") || "";
    let parsed;
    try {
      parsed = contentType.includes("application/json")
        ? await res.json()
        : await res.text();
    } catch (err) {
      parsed = `Invalid response body: ${err.message}`;
    }
    return { ok: res.ok, status: res.status, body: parsed, contentType };
  }

  async function fetchProjects() {
    setLoading(true);
    try {
      const { ok, body } = await apiFetch(`${process.env.REACT_APP_API_URL}/api/projects`, {
        method: "GET",
        json: false,
      });
      if (!ok) throw new Error(body?.error || "Failed to fetch projects");
      setProjects(Array.isArray(body) ? body : []);
    } catch (err) {
      console.error(err.message);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }

  async function fetchClients() {
    try {
      const { ok, body } = await apiFetch(`${process.env.REACT_APP_API_URL}/api/clients`, {
        method: "GET",
        json: false,
      });
      if (!ok) throw new Error(body?.error || "Failed to fetch clients");
      setClients(Array.isArray(body) ? body : []);
    } catch (err) {
      setClients([]);
      console.error(err.message);
    }
  }

  async function fetchManagers() {
    try {
      const { ok, body } = await apiFetch(
        `${process.env.REACT_APP_API_URL}/api/users?role=manager`,
        { method: "GET", json: false }
      );
      if (!ok) throw new Error(body?.error || "Failed to fetch managers");
      setManagers(Array.isArray(body) ? body : []);
    } catch (err) {
      setManagers([]);
      console.error(err.message);
    }
  }

  async function fetchAssets(projectId) {
    try {
      const { ok, body } = await apiFetch(
        `${process.env.REACT_APP_API_URL}/api/project-assets?project_id=${projectId}`,
        { method: "GET", json: false }
      );
      if (!ok) throw new Error(body?.error || "Failed to fetch assets");
      setAssets(Array.isArray(body) ? body : []);
    } catch (err) {
      console.error(err.message);
      setAssets([]);
    }
  }

  useEffect(() => {
    fetchProjects();
    fetchClients();
    fetchManagers();
  }, []);

  function openProjectDetail(project) {
    setSelectedProject(project);
    setShowDetail(true);
    fetchAssets(project.id);
  }

  function handleDetailOverlay(e) {
    if (detailRef.current && !detailRef.current.contains(e.target)) {
      setShowDetail(false);
      setSelectedProject(null);
      setAssets([]);
    }
  }

  function formatCurrency(amount, currency) {
    if (amount == null || amount === "") return "-";
    const num = Number(amount);
    if (Number.isNaN(num)) return String(amount);
    try {
      if (currency) {
        return new Intl.NumberFormat(undefined, {
          style: "currency",
          currency,
          maximumFractionDigits: 2,
        }).format(num);
      }
    } catch (err) {}
    return new Intl.NumberFormat().format(num);
  }

  const filteredProjects = projects.filter((p) => {
    const q = search.trim().toLowerCase();
    return (
      !q ||
      (p.name || "").toLowerCase().includes(q) ||
      (p.status || "").toLowerCase().includes(q) ||
      (p.client_id != null ? String(p.client_id) : "").includes(q)
    );
  });

  return (
    <div className="nmproj-container">
      <div className="nmproj-header-row">
        <h2 className="nmproj-title">Nam-Mark Projects (Admin View)</h2>
        <div className="nmproj-header-actions">
          <div className="nmproj-search-wrapper">
            <Search size={18} className="nmproj-search-icon" />
            <input
              type="text"
              placeholder="Search projects..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="nmproj-search-input"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <p className="nmproj-loading-text">Loading projects...</p>
      ) : (
        <div className="nmproj-table-wrapper">
          <table className="nmproj-table" role="table" aria-label="Projects table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Client</th>
                <th>Description</th>
                <th>Budget</th>
                <th>Total Received</th>
                <th>Deadline</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.length > 0 ? (
                filteredProjects.map((p) => (
                  <tr key={p.id} className="nmproj-row">
                    <td onClick={() => openProjectDetail(p)}>{p.name}</td>
                    <td onClick={() => openProjectDetail(p)}>
                      {clients.find((c) => c.id === p.client_id)?.name || "-"}
                    </td>
                    <td
                      onClick={() => openProjectDetail(p)}
                      className="nmproj-desc-cell"
                    >
                      {p.description
                        ? p.description.slice(0, 80) +
                          (p.description.length > 80 ? "..." : "")
                        : "-"}
                    </td>
                    <td onClick={() => openProjectDetail(p)}>
                      {p.budget != null ? p.budget : "-"}
                    </td>
                    <td onClick={() => openProjectDetail(p)}>
                      {p.total_received != null ? p.total_received : "-"}
                    </td>
                    <td onClick={() => openProjectDetail(p)}>
                      {p.deadline ? p.deadline.split("T")[0] : "-"}
                    </td>
                    <td onClick={() => openProjectDetail(p)}>
                      {p.status || "-"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="nmproj-empty">No projects found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* project detail modal */}
      {showDetail && selectedProject && (
        <div
          className="nmproj-modal-overlay"
          onMouseDown={handleDetailOverlay}
        >
          <div
            className="nmproj-modal nmproj-large"
            ref={detailRef}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              className="nmproj-modal-close"
              aria-label="Close project details"
              onClick={() => {
                setShowDetail(false);
                setSelectedProject(null);
                setAssets([]);
              }}
            >
              <X size={20} />
            </button>

            <div className="nmproj-detail-header">
              <div className="nmproj-detail-title">
                <h3 style={{ margin: 0 }}>
                  Project Details — {selectedProject.name}
                </h3>
                <div style={{ marginTop: 8, color: "#374151" }}>
                  <small>
                    {clients.find((c) => c.id === selectedProject.client_id)
                      ?.name || "-"}{" "}
                    • {selectedProject.status || "-"}
                  </small>
                </div>
              </div>

              <div className="nmproj-budget" aria-hidden>
                <div className="nmproj-budget-amount">
                  {formatCurrency(selectedProject.budget, selectedProject.currency)}
                </div>
                <div className="nmproj-budget-sub">
                  {selectedProject.advance_paid != null
                    ? `Advance: ${formatCurrency(
                        selectedProject.advance_paid,
                        selectedProject.currency
                      )}`
                    : "No advance"}
                </div>
              </div>
            </div>

            <div className="nmproj-detail-grid" style={{ marginTop: 8 }}>
              <div>
                <strong>ID:</strong> {selectedProject.id}
              </div>
              <div>
                <strong>Client:</strong>{" "}
                {clients.find((c) => c.id === selectedProject.client_id)?.name ||
                  "-"}
              </div>
              <div>
                <strong>Manager:</strong>{" "}
                {selectedProject.manager_id === user.id
                  ? user.name
                  : managers.find((m) => m.id === selectedProject.manager_id)
                      ?.name || "-"}
              </div>
              <div>
                <strong>Status:</strong> {selectedProject.status}
              </div>
              <div>
                <strong>Start:</strong>{" "}
                {selectedProject.start_date
                  ? selectedProject.start_date.split("T")[0]
                  : "-"}
              </div>
              <div>
                <strong>Deadline:</strong>{" "}
                {selectedProject.deadline
                  ? selectedProject.deadline.split("T")[0]
                  : "-"}
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <strong>Description:</strong>
                <p>{selectedProject.description || "-"}</p>
              </div>
              <div>
                <strong>Advance Paid:</strong>{" "}
                {selectedProject.advance_paid != null
                  ? formatCurrency(
                      selectedProject.advance_paid,
                      selectedProject.currency
                    )
                  : "-"}
              </div>
              <div>
                <strong>Total Received:</strong>{" "}
                {selectedProject.total_received != null
                  ? formatCurrency(
                      selectedProject.total_received,
                      selectedProject.currency
                    )
                  : "-"}
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <strong>Upsellings:</strong>
                <p>{selectedProject.upsellings || "-"}</p>
              </div>
            </div>

            <hr />

            <div>
              <h4>Assets for this project</h4>
              {assets.length === 0 ? (
                <p className="nmproj-no-assets">No assets yet for this project.</p>
              ) : (
                <div className="nmproj-assets-list">
                  <table className="nmproj-assets-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Provider</th>
                        <th>Purchase</th>
                        <th>Expiry</th>
                        <th>Cost</th>
                        <th>Status</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assets.map((a) => (
                        <tr key={a.id}>
                          <td>{a.name}</td>
                          <td>{a.provider || "-"}</td>
                          <td>{a.purchase_date ? a.purchase_date.split("T")[0] : "-"}</td>
                          <td>{a.expiry_date ? a.expiry_date.split("T")[0] : "-"}</td>
                          <td>{a.cost != null ? a.cost : "-"} {a.currency || ""}</td>
                          <td>{a.status}</td>
                          <td className="nmproj-assets-notes">
                            {a.notes
                              ? a.notes.slice(0, 60) + (a.notes.length > 60 ? "..." : "")
                              : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
